// Fire-and-forget promo analytics beacons to Sons Hub.
// Never throws, never logs, never awaited by callers.
import { getOverridesByPrefix } from "@/lib/copyOverrides";

const ENDPOINT = "https://jhgvpqnesquocndbzygl.supabase.co/functions/v1/promo-events";
const SITE = "sons-island";

export type PromoSurface = "banner" | "popup";
export type PromoEvent = "view" | "click";

function shouldSkip(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1" || host.includes("lovable")) return true;
    if (new URLSearchParams(window.location.search).has("promo_preview")) return true;
  } catch {
    return true;
  }
  return false;
}

async function hashValues(surface: PromoSurface): Promise<string> {
  const values = getOverridesByPrefix(`${surface}.`);
  const sorted = Object.fromEntries(
    Object.entries(values).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
  const input = Object.keys(sorted).length ? JSON.stringify(sorted) : "{}";
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function sendPromoEvent(surface: PromoSurface, event: PromoEvent): void {
  if (shouldSkip()) return;
  try {
    void (async () => {
      try {
        const body = JSON.stringify({
          site: SITE,
          surface,
          event,
          hash: await hashValues(surface),
          path: window.location.pathname,
          ts: Date.now(),
        });
        const blob = new Blob([body], { type: "application/json" });
        if (!(navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob))) {
          await fetch(ENDPOINT, {
            method: "POST",
            keepalive: true,
            headers: { "Content-Type": "application/json" },
            body,
          });
        }
      } catch {
        /* silent */
      }
    })();
  } catch {
    /* silent */
  }
}

const seenViews = new Set<string>();

export function sendPromoViewOnce(surface: PromoSurface, path: string): void {
  const k = `${surface}:${path}`;
  if (seenViews.has(k)) return;
  seenViews.add(k);
  sendPromoEvent(surface, "view");
}
